import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

type AuthMode = "login" | "signup";
type LoginMethod = "email" | "phone";

const FactoryAuthPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<AuthMode>("login");
  const [loginMethod, setLoginMethod] = useState<LoginMethod>("email");
  const [step, setStep] = useState<"form" | "otp">("form");
  const [loading, setLoading] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);

  const [formData, setFormData] = useState({
    companyName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    country: "USA",
    agreeTerms: false,
  });

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      if (mode === "signup" && loginMethod === "phone") {
        setStep("otp");
      } else {
        navigate("/factory/dashboard");
      }
    }, 1200);
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const newOtp = [...otpValues];
    newOtp[index] = value;
    setOtpValues(newOtp);
    if (value && index < 5) {
      const next = document.getElementById(`otp-${index + 1}`);
      next?.focus();
    }
  };

  const handleOtpVerify = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      navigate("/factory/dashboard");
    }, 1000);
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/0e540cccecc240c8e6899a61044bd1df.jpg"
          alt="Factory"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a0a0a]/80 via-[#C41E3A]/40 to-[#1a0a0a]/70"></div>
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
              alt="Crown Prime Mall"
              className="h-10 w-10 rounded-full object-cover"
            />
            <div>
              <div className="text-white font-bold text-base">Crown Prime Mall</div>
              <div className="text-[#D4AF37] text-xs">Factory Supplier Portal</div>
            </div>
          </Link>

          <div>
            <h2 className="text-white text-4xl font-bold leading-tight mb-4">
              Sell to Buyers<br />Across USA &<br />Canada
            </h2>
            <p className="text-white/70 text-sm mb-8 leading-relaxed">
              Join thousands of verified factory suppliers. Upload your products, manage orders, and grow your business on North America's premier B2B marketplace.
            </p>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: "ri-store-2-line", label: "50,000+", sub: "Active Buyers" },
                { icon: "ri-shield-check-line", label: "Verified", sub: "Supplier Badge" },
                { icon: "ri-truck-line", label: "USA & Canada", sub: "Warehouses" },
                { icon: "ri-money-dollar-circle-line", label: "USD Payments", sub: "Secure Checkout" },
              ].map((item) => (
                <div key={item.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="w-8 h-8 flex items-center justify-center mb-2">
                    <i className={`${item.icon} text-[#D4AF37] text-xl`}></i>
                  </div>
                  <div className="text-white font-bold text-sm">{item.label}</div>
                  <div className="text-white/60 text-xs">{item.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-white px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
                alt="Crown Prime Mall"
                className="h-9 w-9 rounded-full object-cover"
              />
              <div>
                <div className="text-[#C41E3A] font-bold text-sm">Crown Prime Mall</div>
                <div className="text-gray-400 text-xs">Factory Portal</div>
              </div>
            </Link>
          </div>

          {step === "otp" ? (
            /* OTP Verification */
            <div>
              <div className="w-14 h-14 flex items-center justify-center bg-[#C41E3A]/10 rounded-2xl mb-6">
                <i className="ri-smartphone-line text-[#C41E3A] text-2xl"></i>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Phone</h1>
              <p className="text-gray-500 text-sm mb-8">
                We sent a 6-digit code to <span className="font-semibold text-gray-700">{formData.phone}</span>
              </p>
              <div className="flex gap-3 mb-6">
                {otpValues.map((val, i) => (
                  <input
                    key={i}
                    id={`otp-${i}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={val}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    className="w-12 h-14 text-center text-xl font-bold border-2 border-gray-200 rounded-xl focus:border-[#C41E3A] outline-none transition-colors"
                  />
                ))}
              </div>
              <button
                onClick={handleOtpVerify}
                disabled={loading || otpValues.some((v) => !v)}
                className="w-full bg-[#C41E3A] text-white py-3 rounded-xl font-semibold text-sm hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i> Verifying...
                  </span>
                ) : "Verify & Create Account"}
              </button>
              <button
                onClick={() => setStep("form")}
                className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700 cursor-pointer py-2"
              >
                <i className="ri-arrow-left-line mr-1"></i> Back
              </button>
            </div>
          ) : (
            /* Auth Form */
            <>
              {/* Mode Toggle */}
              <div className="flex bg-gray-100 rounded-xl p-1 mb-8">
                <button
                  onClick={() => setMode("login")}
                  className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                    mode === "login" ? "bg-white text-[#C41E3A] shadow-sm" : "text-gray-500"
                  }`}
                >
                  Sign In
                </button>
                <button
                  onClick={() => setMode("signup")}
                  className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                    mode === "signup" ? "bg-white text-[#C41E3A] shadow-sm" : "text-gray-500"
                  }`}
                >
                  Create Account
                </button>
              </div>

              <h1 className="text-2xl font-bold text-gray-900 mb-1">
                {mode === "login" ? "Welcome back" : "Join as a Supplier"}
              </h1>
              <p className="text-gray-500 text-sm mb-6">
                {mode === "login"
                  ? "Sign in to your factory supplier account"
                  : "Start selling to buyers across USA & Canada"}
              </p>

              {/* Login Method Toggle */}
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => setLoginMethod("email")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                    loginMethod === "email"
                      ? "bg-[#C41E3A] text-white border-[#C41E3A]"
                      : "border-gray-200 text-gray-600 hover:border-[#C41E3A]"
                  }`}
                >
                  <i className="ri-mail-line"></i> Email
                </button>
                <button
                  onClick={() => setLoginMethod("phone")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                    loginMethod === "phone"
                      ? "bg-[#C41E3A] text-white border-[#C41E3A]"
                      : "border-gray-200 text-gray-600 hover:border-[#C41E3A]"
                  }`}
                >
                  <i className="ri-smartphone-line"></i> Phone
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === "signup" && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Company / Factory Name</label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                        <i className="ri-building-2-line text-gray-400 text-sm"></i>
                      </div>
                      <input
                        type="text"
                        required
                        value={formData.companyName}
                        onChange={(e) => handleChange("companyName", e.target.value)}
                        placeholder="e.g. GreenGrow USA Manufacturing"
                        className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none transition-colors"
                      />
                    </div>
                  </div>
                )}

                {loginMethod === "email" ? (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email Address</label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                        <i className="ri-mail-line text-gray-400 text-sm"></i>
                      </div>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        placeholder="factory@company.com"
                        className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none transition-colors"
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Phone Number</label>
                    <div className="flex gap-2">
                      <select
                        className="border border-gray-200 rounded-xl px-3 py-3 text-sm text-gray-700 outline-none focus:border-[#C41E3A] cursor-pointer"
                        defaultValue="+1"
                      >
                        <option value="+1">🇺🇸 +1</option>
                        <option value="+1ca">🇨🇦 +1</option>
                        <option value="+86">🇨🇳 +86</option>
                        <option value="+44">🇬🇧 +44</option>
                      </select>
                      <div className="relative flex-1">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                          <i className="ri-smartphone-line text-gray-400 text-sm"></i>
                        </div>
                        <input
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => handleChange("phone", e.target.value)}
                          placeholder="(555) 000-0000"
                          className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none transition-colors"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {(mode === "login" && loginMethod === "email") || (mode === "signup" && loginMethod === "email") ? (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                        <i className="ri-lock-line text-gray-400 text-sm"></i>
                      </div>
                      <input
                        type="password"
                        required
                        value={formData.password}
                        onChange={(e) => handleChange("password", e.target.value)}
                        placeholder="••••••••"
                        className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none transition-colors"
                      />
                    </div>
                  </div>
                ) : null}

                {mode === "signup" && loginMethod === "email" && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Confirm Password</label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                        <i className="ri-lock-2-line text-gray-400 text-sm"></i>
                      </div>
                      <input
                        type="password"
                        required
                        value={formData.confirmPassword}
                        onChange={(e) => handleChange("confirmPassword", e.target.value)}
                        placeholder="••••••••"
                        className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none transition-colors"
                      />
                    </div>
                  </div>
                )}

                {mode === "signup" && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Country</label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                        <i className="ri-map-pin-line text-gray-400 text-sm"></i>
                      </div>
                      <select
                        value={formData.country}
                        onChange={(e) => handleChange("country", e.target.value)}
                        className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#C41E3A] outline-none transition-colors cursor-pointer appearance-none"
                      >
                        <option value="USA">🇺🇸 United States</option>
                        <option value="Canada">🇨🇦 Canada</option>
                        <option value="China">🇨🇳 China</option>
                        <option value="Other">🌍 Other</option>
                      </select>
                    </div>
                  </div>
                )}

                {mode === "signup" && (
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.agreeTerms}
                      onChange={(e) => handleChange("agreeTerms", e.target.checked)}
                      className="mt-0.5 accent-[#C41E3A]"
                      required
                    />
                    <span className="text-xs text-gray-500 leading-relaxed">
                      I agree to the{" "}
                      <a href="#" className="text-[#C41E3A] hover:underline">Terms of Service</a>{" "}
                      and{" "}
                      <a href="#" className="text-[#C41E3A] hover:underline">Supplier Agreement</a>
                    </span>
                  </label>
                )}

                {mode === "login" && (
                  <div className="flex justify-end">
                    <a href="#" className="text-xs text-[#C41E3A] hover:underline">Forgot password?</a>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#C41E3A] text-white py-3 rounded-xl font-semibold text-sm hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 mt-2"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      {mode === "login" ? "Signing in..." : "Creating account..."}
                    </span>
                  ) : mode === "login" ? "Sign In to Dashboard" : loginMethod === "phone" ? "Send Verification Code" : "Create Supplier Account"}
                </button>
              </form>

              <div className="mt-6 pt-6 border-t border-gray-100 text-center">
                <p className="text-xs text-gray-500">
                  {mode === "login" ? "Don't have an account?" : "Already have an account?"}{" "}
                  <button
                    onClick={() => setMode(mode === "login" ? "signup" : "login")}
                    className="text-[#C41E3A] font-semibold hover:underline cursor-pointer"
                  >
                    {mode === "login" ? "Create one" : "Sign in"}
                  </button>
                </p>
              </div>

              <div className="mt-4 text-center">
                <Link to="/" className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-arrow-left-line mr-1"></i> Back to Crown Prime Mall
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FactoryAuthPage;
